import { AppConfig } from '@/config/app';
import logger from '@/lib/edge-logger';
import { ThingCategory, ThingDefinition } from '@/types/setting';
import { User } from '@/types/user';
import { del, get, Method, post, put, request } from '@/utils/api-util';

/**
 * 列出所有主数据
 * @param params 分类代码
 * @returns
 */
export const listMasterdatas = async (params: { categoryCode: string; sortBy: string; sortAs: string; page?: number; size?: number }) => {
    return (await get('/api/v1/masterdatas', params, {
        loadingMessage: '正在加载主数据',
        successMessage: '主数据加载成功'
    })).data;
}

/**
 * 获取主数据定义
 * @param masterdataCode 主数据代码
 * @param definitionVersion 定义版本
 * @returns
 */
export const getMasterDataDefinition = async (masterdataCode: string, definitionVersion?: string) => {
    return (await get(`/api/v1/masterdata/${masterdataCode}/definition/${definitionVersion}`, undefined, {
        loadingMessage: '正在加载主数据定义',
        successMessage: '主数据定义加载成功'
    })).data;
}

/**
 * 列出主数据记录
 * @param masterdataCode 主数据代码
 * @param recordsVersion 记录版本
 * @param queryParams
 * @param useCache
 * @returns
 */
export const listMasterdataRecords = async (
    masterdataCode: string, recordsVersion?: string, queryParams?: {
        queryField?: string;
        queryValue?: string;
        page?: number;
        size?: number;
        status?: string;
        ids?: (string | number)[];
    }, useCache?: boolean) => {
    const params: Record<string, string> = {};
    if (queryParams?.queryField && queryParams?.queryValue) {
        params.queryField = queryParams.queryField;
        params.queryValue = queryParams.queryValue;
    }
    if (queryParams?.page && queryParams?.size) {
        params.page = queryParams.page.toString();
        params.size = queryParams.size.toString();
    }
    if (queryParams?.status) {
        params.status = queryParams.status;
    }
    if (queryParams?.ids?.length) {
        params.ids = queryParams.ids.join(',');
    }

    return (await get(`/api/v1/masterdata/${masterdataCode}/records/${recordsVersion}`, params, {
        loadingMessage: '正在加载主数据记录',
        successMessage: '主数据记录加载成功',
        ...useCache ? { headers: { [AppConfig.headers.useCache]: 'true' } } : {}
    })).data;
}

/**
 * 更新主数据记录
 * @param masterdataCode 主数据代码
 * @param masterdataRecordId 记录ID
 * @param record 主数据记录
 * @returns
 */
export const updateMasterDataRecord = async (masterdataCode: string, masterdataRecordId: string, record: any, options?: { silent?: boolean }) => {
    return (await put(`/api/v1/masterdata/${masterdataCode}/record`, record, { id: masterdataRecordId }, {
        loadingMessage: '正在修改主数据记录',
        successMessage: options?.silent ? undefined : '主数据记录修改成功',
        headers: options?.silent ? { [AppConfig.headers.useApiTip]: 'false' } : undefined,
    })).data;
}

/**
 * 删除主数据记录
 * @param masterDataCode 主数据代码
 * @param recordId 记录ID
 * @returns
 */
export const deleteMasterDataRecord = async (masterDataCode: string, recordId: string) => {
    return (await del(`/api/v1/masterdata/${masterDataCode}/record`, { id: recordId }, {
        loadingMessage: '正在删除主数据记录',
        successMessage: '主数据记录删除成功'
    })).data;
}

/**
 * 获取主数据记录
 * @param masterdataCode 主数据代码
 * @param recordsVersion 记录版本
 * @param idContend 记录ID
 * @returns
 */
/**
 * 获取主数据记录
 * @param masterdataCode 主数据代码
 * @param recordsVersion 记录版本
 * @param idContent 记录ID
 * @param options 额外选项
 * @returns
 */
export const getMasterDataRecord = async (masterdataCode: string, recordsVersion: string, idContent: string, options?: { useApiTip?: boolean }) => {
    return (await get(`/api/v1/masterdata/${masterdataCode}/record`, {
        version: recordsVersion,
        idContent: idContent
    })).data;
}

export async function listDefinitions(category: ThingCategory, user: User | null): Promise<ThingDefinition[]> {
    logger.info('当前登录用户:', user); // 打印用户信息用于验证
    const response = await request(`/api/masterdata/category/${category.id}/definitions`, {}, undefined, '获取主数据定义成功', Method.GET);
    return response.data;
}

/**
 * 创建主数据记录
 * @param masterdataCode 主数据代码
 * @param record 主数据记录
 * @returns
 */
export const createMasterDataRecord = async (masterdataCode: string, record: any) => {
    return (await post(`/api/v1/masterdata/${masterdataCode}/record`, record, undefined, {
        loadingMessage: '正在创建主数据记录',
        successMessage: '主数据记录创建成功'
    })).data;
}

/**
 * 批量创建主数据记录
 * @param masterdataCode 主数据代码
 * @param record 主数据记录
 * @returns
 */
export const batchCreateMasterDataRecord = async (masterdataCode: string, record: any) => {
    return (await post(`/api/v1/masterdata/${masterdataCode}/batch-record`, record, undefined, {
        loadingMessage: '正在批量创建主数据记录',
        successMessage: '主数据记录批量创建成功'
    })).data;
}

/**
 * 获取主数据记录
 * @param type 主数据类型
 * @returns 
 */
export const listMasterdataContent = async (type: string, useCache = false) => {
    const { items } = await listMasterdataRecords(type, '1.0', { page: 1, size: 1000, status: 'ON' }, useCache);
    return items.map((item: { content: any; }) => item.content);
}

/**
/**
 * 删除指定主数据的所有缓存
 * @param masterdataCode 主数据代码，用于标识需要刷新缓存的主数据
 * @returns 包含删除缓存操作结果的响应数据
 */
export const deleteMasterdataAllCache = async (masterdataCode: string) => {
    return (await del(`/api/v1/masterdata/${masterdataCode}/cache`, undefined)).data;
}

/**
 * 批量获取主数据记录作为选项
 * @param batchParams 批量参数数组，包含masterdataCode和version
 * @param status 记录状态，默认为'ON'
 * @returns 返回格式为 { [masterdataCode]: options[] }
 */
export const batchListMasterDataAsOptions = async (
    batchParams: Array<{ masterdataCode: string; version?: string, parentCode?: string }>,
    status: string = 'ON'
) => {
    return (await post('/api/v1/masterdatas/options', { batchParams, status })).data;
};

/**
 * 获取主数据记录的所有版本历史
 * @param masterdataCode 主数据代码
 * @param valueContent 记录的值内容（模板编码）
 * @returns 版本列表
 */
const silentTipHeaders = { [AppConfig.headers.useApiTip]: 'false' };

export const listMasterDataRecordVersions = async (masterdataCode: string, valueContent: string) => {
    return (await get(`/api/v1/masterdata/${masterdataCode}/record/version`, { valueContent }, { headers: silentTipHeaders })).data;
}

/**
 * 将当前记录复制为一个新版本（快照）
 * @param masterdataCode 主数据代码
 * @param recordId 记录ID
 * @returns 新版本记录
 */
export const copyMasterDataRecordVersion = async (masterdataCode: string, recordId: number | string) => {
    return (await post(`/api/v1/masterdata/${masterdataCode}/record/version`, { recordId }, undefined, { headers: silentTipHeaders })).data;
}

/**
 * 复制创建：以当前记录为蓝本创建新模板（code/name 由用户重写）
 * @param masterdataCode 主数据代码
 * @param recordId 源记录ID
 * @param code 新模板代码（同时写入 id_content/value_content）
 * @param name 新模板名称（同时写入 label_content）
 * @returns 新创建的记录
 */
export const copyMasterDataRecord = async (masterdataCode: string, recordId: number | string, code: string, name: string) => {
    return (await post(`/api/v1/masterdata/${masterdataCode}/record/copy`, { recordId, code, name }, undefined, { headers: silentTipHeaders })).data;
}
